import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { EmptyState } from '../../../components/PageState';
import { Badge, Button, FilterChips, Input, ProgressBar, SectionCard, Select, type ChipOption } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatDate, formatDateTime, formatPercentFromBps } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import { custoDoEnvio } from '../comparar';
import {
  LOTE_DA_TABELA,
  alternarOrdem,
  contarFacetas,
  custoPorRecuperacao,
  filtrarEnvios,
  optOutBps,
  ordenarEnvios,
  type ColunaEnvios,
  type EstadoTabelaEnvios,
} from '../tabelaEnvios';
import type { Broadcast, BroadcastStatus, SenderKind } from '../types';
import {
  BROADCAST_STATUS_LABEL,
  BROADCAST_STATUS_TONE,
  PAUSED_REASON_LABEL,
  SEGMENT_SHORT_LABEL,
  SENDER_KIND_SHORT,
  TEMPLATE_CATEGORY_LABEL,
  TEMPLATE_CATEGORY_TONE,
  idCurto,
} from '../vocabulario';
import { ColunaOrdenavel } from './ColunaOrdenavel';

/**
 * Lista de envios em massa, pronta para centenas de empresas.
 *
 * Decisoes nao obvias:
 *  1. Busca (atalho "/"), empresa, status e canal se COMBINAM; a contagem de
 *     cada chip considera os outros filtros (ver tabelaEnvios.ts).
 *  2. Ordenacao por coluna no cabecalho (desktop) e num Select (sempre
 *     visivel), que tambem da acesso a "maior opt-out" -- coluna que nao
 *     cabe na tabela mas importa para proteger o numero.
 *  3. Retorno por linha: recuperado, pagaram, quantos a vendedora fechou
 *     ("fechado por", E5) e se a janela de 7 dias ainda esta aberta -- o
 *     numero ainda pode subir.
 *  4. Duas formas do MESMO array (tabela no `md+`, cartoes abaixo), como na
 *     LeadsTable. "Ver mais" em lotes de 10.
 *  5. Retomar um envio pausado pela trava de opt-out DESLIGA a trava dele
 *     (senao pausaria de novo no proximo tique); o botao diz isso.
 *  6. Clique duplo: "Pausar" vira "Retomar" (e "Iniciar agora" vira
 *     "Pausar") NO MESMO LUGAR. Sem trava, o segundo clique do duplo desfaria
 *     o primeiro. As acoes da linha ficam travadas por `TRAVA_MS` depois de
 *     cada clique.
 */

export interface AcoesDoEnvio {
  readonly onPausar: (id: string) => void;
  readonly onRetomar: (id: string, semTrava: boolean) => void;
  readonly onIniciarAgora: (id: string) => void;
  readonly onRelatorio: (id: string) => void;
}

const DIA_MS = 86_400_000;
/** Maior que o intervalo de um clique duplo (~500 ms no Windows). */
const TRAVA_MS = 700;

const ORDENS: readonly { readonly value: string; readonly label: string; readonly coluna: ColunaEnvios; readonly desc: boolean }[] = [
  { value: 'criado-desc', label: 'Mais recentes', coluna: 'criado', desc: true },
  { value: 'recuperado-desc', label: 'Maior recuperado', coluna: 'recuperado', desc: true },
  { value: 'custo_por_recuperacao-asc', label: 'Menor custo por recuperação', coluna: 'custo_por_recuperacao', desc: false },
  { value: 'progresso-asc', label: 'Menos avançados', coluna: 'progresso', desc: false },
  { value: 'optout-desc', label: 'Maior opt-out', coluna: 'optout', desc: true },
];

function janelaAbertaAte(e: Broadcast, agora: Date): string | null {
  if (e.startedAt === null || e.status === 'agendada') {
    return null;
  }
  const base = new Date(e.finishedAt ?? agora.toISOString()).getTime();
  const fim = base + 7 * DIA_MS;
  return fim > agora.getTime() ? new Date(fim).toISOString() : null;
}

function publicoCurto(e: Broadcast): string {
  if (e.audience.source === 'csv') {
    return 'Lista importada';
  }
  return e.audience.segments.map((k) => SEGMENT_SHORT_LABEL[k]).join(' + ') || 'Públicos da base';
}

function Progresso({ envio }: { readonly envio: Broadcast }): JSX.Element {
  const opt = optOutBps(envio);
  return (
    <div className="flex flex-col gap-1">
      <ProgressBar value={envio.sentCount} max={envio.totalContacts} tone={BROADCAST_STATUS_TONE[envio.status]} label={`Progresso de ${envio.name}`} />
      <p className="text-xs tabular-nums text-ink-subtle">
        {formatCount(envio.sentCount)}/{formatCount(envio.totalContacts)} enviados · {formatCount(envio.failedCount)} falhas · {formatCount(envio.optOutCount)} opt-out
        {opt !== null && opt >= 300 ? <span className="text-status-critical"> ({formatPercentFromBps(opt)})</span> : null}
      </p>
    </div>
  );
}

function Retorno({ envio, agora }: { readonly envio: Broadcast; readonly agora: Date }): JSX.Element {
  const aberta = janelaAbertaAte(envio, agora);
  if (envio.status === 'agendada') {
    return <p className="text-xs text-ink-subtle">Começa em {formatDateTime(envio.scheduledFor)}</p>;
  }
  return (
    <div className="flex flex-col gap-0.5">
      <p className="font-medium tabular-nums text-ink">{formatBRLFromCents(envio.recoveredAmountCents)}</p>
      <p className="text-xs text-ink-subtle">
        {formatCount(envio.paidCount)} {envio.paidCount === 1 ? 'pagou' : 'pagaram'}
        {envio.paidViaSellerCount > 0 ? ` · ${formatCount(envio.paidViaSellerCount)} com a vendedora` : ''}
      </p>
      {aberta === null ? null : <p className="text-xs text-info">janela aberta até {formatDate(aberta)}</p>}
    </div>
  );
}

function CustoPorRecuperacao({ envio }: { readonly envio: Broadcast }): JSX.Element {
  const custo = custoDoEnvio(envio);
  const unitario = custoPorRecuperacao(envio);
  return (
    <div className="flex flex-col gap-0.5 text-right">
      <p className="font-medium tabular-nums text-ink">{custo === 0 ? 'sem custo' : unitario === null ? '—' : formatBRLFromCents(unitario)}</p>
      <p className="text-xs text-ink-subtle">{custo === 0 ? 'número do comercial' : `${formatBRLFromCents(custo)} no total`}</p>
    </div>
  );
}

function Acoes({ envio, acoes }: { readonly envio: Broadcast; readonly acoes: AcoesDoEnvio }): JSX.Element {
  const [travado, setTravado] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
      }
    },
    [],
  );
  const executar = (acao: () => void): void => {
    if (travado) {
      return;
    }
    setTravado(true);
    acao();
    timer.current = window.setTimeout(() => setTravado(false), TRAVA_MS);
  };
  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      {envio.status === 'em_andamento' ? (
        <Button variant="secondary" size="sm" icon="pausa" disabled={travado} onClick={() => executar(() => acoes.onPausar(envio.id))} aria-label={`Pausar ${envio.name}`}>
          Pausar
        </Button>
      ) : null}
      {envio.status === 'pausada' ? (
        envio.pausedReason === 'optout_alto' ? (
          <Button
            variant="secondary"
            size="sm"
            icon="play"
            disabled={travado}
            onClick={() => executar(() => acoes.onRetomar(envio.id, true))}
            title="Retoma com a trava de opt-out desligada para este envio"
            aria-label={`Retomar ${envio.name} sem a trava de opt-out`}
          >
            Retomar sem trava
          </Button>
        ) : (
          <Button variant="secondary" size="sm" icon="play" disabled={travado} onClick={() => executar(() => acoes.onRetomar(envio.id, false))} aria-label={`Retomar ${envio.name}`}>
            Retomar
          </Button>
        )
      ) : null}
      {envio.status === 'agendada' ? (
        <Button variant="secondary" size="sm" icon="play" disabled={travado} onClick={() => executar(() => acoes.onIniciarAgora(envio.id))} aria-label={`Iniciar ${envio.name} agora`}>
          Iniciar agora
        </Button>
      ) : null}
      <Button variant="ghost" size="sm" icon="grafico" onClick={() => acoes.onRelatorio(envio.id)} aria-label={`Relatório de ${envio.name}`}>
        Relatório
      </Button>
    </div>
  );
}

function Titulo({ envio, empresa }: { readonly envio: Broadcast; readonly empresa: string | null }): JSX.Element {
  return (
    <>
      <p className="font-medium text-ink">{envio.name}</p>
      <p className="text-xs text-ink-subtle">
        {empresa === null ? '' : `${empresa} · `}
        {formatDateTime(envio.createdAt)} · {idCurto(envio.id)}
      </p>
    </>
  );
}

export function TabelaDeEnvios({
  envios,
  companies,
  estado,
  onEstado,
  acoes,
  agora,
}: {
  readonly envios: readonly Broadcast[];
  readonly companies: readonly CompanySummary[];
  readonly estado: EstadoTabelaEnvios;
  readonly onEstado: (patch: Partial<EstadoTabelaEnvios>) => void;
  readonly acoes: AcoesDoEnvio;
  readonly agora: Date;
}): JSX.Element {
  const buscaId = useId();
  const nomeEmpresa = useMemo(() => {
    const mapa = new Map(companies.map((c) => [c.id, c.name]));
    return (id: string): string => mapa.get(id) ?? 'Empresa';
  }, [companies]);

  const filtro = { busca: estado.busca, empresa: estado.empresa, status: estado.status, canal: estado.canal };
  const filtrados = filtrarEnvios(envios, filtro, nomeEmpresa);
  const facetas = contarFacetas(envios, filtro, nomeEmpresa);
  const ordenados = ordenarEnvios(filtrados, estado.ordem);
  const visiveis = ordenados.slice(0, estado.limite);
  const mostrarEmpresa = estado.empresa === 'todas';
  const daEmpresa = estado.empresa === 'todas' ? envios : envios.filter((e) => e.companyId === estado.empresa);

  // Atalho "/" foca a busca, como em ferramentas de operacao.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      const alvo = event.target;
      if (alvo instanceof HTMLInputElement || alvo instanceof HTMLTextAreaElement || alvo instanceof HTMLSelectElement || (alvo instanceof HTMLElement && alvo.isContentEditable)) {
        return;
      }
      event.preventDefault();
      document.getElementById(buscaId)?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [buscaId]);

  const opcoesStatus: readonly ChipOption<'todos' | BroadcastStatus>[] = [
    { value: 'todos', label: 'Todos', count: formatCount(facetas.status.todos) },
    { value: 'em_andamento', label: 'Em andamento', count: formatCount(facetas.status.em_andamento) },
    { value: 'agendada', label: 'Agendadas', count: formatCount(facetas.status.agendada) },
    { value: 'pausada', label: 'Pausadas', count: formatCount(facetas.status.pausada) },
    { value: 'concluida', label: 'Concluídas', count: formatCount(facetas.status.concluida) },
  ];
  const opcoesCanal: readonly ChipOption<'todos' | SenderKind>[] = [
    { value: 'todos', label: 'Todos', count: formatCount(facetas.canal.todos) },
    { value: 'evolution', label: 'Evolution', count: formatCount(facetas.canal.evolution) },
    { value: 'oficial', label: 'API oficial', count: formatCount(facetas.canal.oficial) },
  ];
  const ordemAtual = ORDENS.find((o) => o.coluna === estado.ordem.coluna && o.desc === estado.ordem.desc)?.value ?? '';
  const ordenarPor = (coluna: ColunaEnvios): void => onEstado({ ordem: alternarOrdem(estado.ordem, coluna), limite: LOTE_DA_TABELA });
  const temFiltro = estado.busca.trim() !== '' || estado.status !== 'todos' || estado.canal !== 'todos';

  return (
    <SectionCard
      id="envios"
      icon="disparo"
      title="Envios"
      description="Envios em massa no período do cabeçalho, com o retorno de cada um. Na ordem padrão (mais recentes), os criados nesta prévia aparecem no topo."
      aside={
        <Badge tone="neutral">
          {formatCount(filtrados.length)} de {formatCount(envios.length)}
        </Badge>
      }
      padded={false}
    >
      <div className="flex flex-col gap-3 px-5 pb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex w-full flex-col gap-1 sm:w-72">
            <label htmlFor={buscaId} className="text-xs font-medium text-ink-subtle">
              Buscar <span className="text-ink-subtle/80">(atalho: /)</span>
            </label>
            <Input id={buscaId} size="sm" type="search" autoComplete="off" placeholder="Envio, modelo, empresa ou #id" value={estado.busca} onChange={(e) => onEstado({ busca: e.target.value, limite: LOTE_DA_TABELA })} />
          </div>
          <label className="flex w-full flex-col gap-1 text-xs font-medium text-ink-subtle sm:w-56">
            Empresa
            <Select size="sm" value={estado.empresa} onChange={(e) => onEstado({ empresa: e.target.value, limite: LOTE_DA_TABELA })}>
              <option value="todas">Todas as empresas</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="flex w-full flex-col gap-1 text-xs font-medium text-ink-subtle sm:w-60">
            Ordenar por
            <Select
              size="sm"
              value={ordemAtual}
              onChange={(e) => {
                const escolhida = ORDENS.find((o) => o.value === e.target.value);
                if (escolhida !== undefined) {
                  onEstado({ ordem: { coluna: escolhida.coluna, desc: escolhida.desc }, limite: LOTE_DA_TABELA });
                }
              }}
            >
              {ordemAtual === '' ? <option value="">Pela coluna escolhida</option> : null}
              {ORDENS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </label>
        </div>
        <FilterChips options={opcoesStatus} value={estado.status} onChange={(status) => onEstado({ status, limite: LOTE_DA_TABELA })} label="Filtrar por status" prefix="Status:" />
        <FilterChips options={opcoesCanal} value={estado.canal} onChange={(canal) => onEstado({ canal, limite: LOTE_DA_TABELA })} label="Filtrar por canal" prefix="Canal:" tone="accent" />
      </div>

      {daEmpresa.length === 0 ? (
        <div className="px-5 pb-5">
          <EmptyState
            title="Nenhum envio no período"
            description={
              estado.empresa === 'todas'
                ? 'Nenhuma empresa disparou neste período. Amplie a janela no cabeçalho ou prepare um envio no formulário acima.'
                : 'Os públicos pós-fato desta empresa estão no formulário acima: marque um, escolha o modelo e inicie o primeiro envio. Ou amplie a janela no cabeçalho.'
            }
            action={
              estado.empresa === 'todas' ? undefined : (
                <Button variant="secondary" size="sm" icon="empresa" onClick={() => onEstado({ empresa: 'todas', limite: LOTE_DA_TABELA })}>
                  Ver todas as empresas
                </Button>
              )
            }
          />
        </div>
      ) : filtrados.length === 0 ? (
        <div className="px-5 pb-5">
          <EmptyState
            title="Nenhum envio com esses filtros"
            description={temFiltro ? 'A busca ou os chips escondem todos os envios. Limpe para ver a lista inteira.' : 'Nenhum envio neste recorte.'}
            action={
              <Button variant="secondary" size="sm" icon="x" onClick={() => onEstado({ busca: '', status: 'todos', canal: 'todos', limite: LOTE_DA_TABELA })}>
                Limpar filtros
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-line text-xs text-ink-subtle">
                  <ColunaOrdenavel coluna="criado" rotulo="Envio" ativa={estado.ordem.coluna === 'criado'} desc={estado.ordem.desc} onOrdenar={ordenarPor} className="pl-5" />
                  <th scope="col" className="px-3 py-2 text-left font-medium uppercase tracking-wide">
                    Modelo e público
                  </th>
                  <th scope="col" className="px-3 py-2 text-left font-medium uppercase tracking-wide">
                    Status
                  </th>
                  <ColunaOrdenavel coluna="progresso" rotulo="Progresso" ativa={estado.ordem.coluna === 'progresso'} desc={estado.ordem.desc} onOrdenar={ordenarPor} className="min-w-[13rem]" />
                  <ColunaOrdenavel coluna="recuperado" rotulo="Retorno" ativa={estado.ordem.coluna === 'recuperado'} desc={estado.ordem.desc} onOrdenar={ordenarPor} />
                  <ColunaOrdenavel
                    coluna="custo_por_recuperacao"
                    rotulo="Custo por recuperação"
                    ativa={estado.ordem.coluna === 'custo_por_recuperacao'}
                    desc={estado.ordem.desc}
                    onOrdenar={ordenarPor}
                    alinhamento="direita"
                  />
                  <th scope="col" className="px-5 py-2 text-right font-medium uppercase tracking-wide">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((envio) => (
                  <tr key={envio.id} className="border-b border-line/70 align-top transition-colors last:border-0 hover:bg-ink/[0.03]">
                    <td className="py-3 pl-5 pr-3">
                      <Titulo envio={envio} empresa={mostrarEmpresa ? nomeEmpresa(envio.companyId) : null} />
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-ink">{envio.templateName}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <Badge tone={TEMPLATE_CATEGORY_TONE[envio.templateCategory]}>{TEMPLATE_CATEGORY_LABEL[envio.templateCategory]}</Badge>
                        <span className="text-xs text-ink-subtle">
                          {publicoCurto(envio)} · {SENDER_KIND_SHORT[envio.senderKind]}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <Badge tone={BROADCAST_STATUS_TONE[envio.status]} dot>
                        {BROADCAST_STATUS_LABEL[envio.status]}
                      </Badge>
                      {envio.pausedReason === null ? null : <p className="mt-1 text-xs text-ink-subtle">{PAUSED_REASON_LABEL[envio.pausedReason]}</p>}
                    </td>
                    <td className="px-3 py-3">
                      <Progresso envio={envio} />
                    </td>
                    <td className="px-3 py-3">
                      <Retorno envio={envio} agora={agora} />
                    </td>
                    <td className="px-3 py-3">
                      <CustoPorRecuperacao envio={envio} />
                    </td>
                    <td className="py-3 pl-3 pr-5">
                      <Acoes envio={envio} acoes={acoes} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="flex flex-col gap-3 px-5 pb-5 md:hidden">
            {visiveis.map((envio) => (
              <li key={envio.id} className="flex flex-col gap-3 rounded-xl border border-line bg-elevated px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Titulo envio={envio} empresa={mostrarEmpresa ? nomeEmpresa(envio.companyId) : null} />
                  </div>
                  <Badge tone={BROADCAST_STATUS_TONE[envio.status]} dot>
                    {BROADCAST_STATUS_LABEL[envio.status]}
                  </Badge>
                </div>
                <p className="flex flex-wrap items-center gap-2 text-sm text-ink-muted">
                  {envio.templateName}
                  <Badge tone={TEMPLATE_CATEGORY_TONE[envio.templateCategory]}>{TEMPLATE_CATEGORY_LABEL[envio.templateCategory]}</Badge>
                  <span className="text-xs text-ink-subtle">
                    {publicoCurto(envio)} · {SENDER_KIND_SHORT[envio.senderKind]}
                  </span>
                </p>
                <Progresso envio={envio} />
                <div className="grid grid-cols-2 gap-3">
                  <Retorno envio={envio} agora={agora} />
                  <CustoPorRecuperacao envio={envio} />
                </div>
                <Acoes envio={envio} acoes={acoes} />
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-xs text-ink-subtle">
            <span>
              Mostrando {formatCount(visiveis.length)} de {formatCount(ordenados.length)}
            </span>
            {ordenados.length > visiveis.length ? (
              <Button variant="secondary" size="sm" icon="chevron" onClick={() => onEstado({ limite: estado.limite + LOTE_DA_TABELA })}>
                Ver mais ({formatCount(Math.min(LOTE_DA_TABELA, ordenados.length - visiveis.length))} de {formatCount(ordenados.length - visiveis.length)} restantes)
              </Button>
            ) : null}
          </div>
        </>
      )}
    </SectionCard>
  );
}
